import { useMemo } from "react";
import { type TableColumnType } from "@ir/ui";
import type { MasterDataDistributionCenter as MasterDataDistributionCenterEntity } from "@src/domain/entities/masterData";
import {
  MASTER_DATA_COLUMN_VERTICAL_ALIGN,
  MasterDataListTable,
  renderMasterDataTextCell,
} from "./masterDataTable.shared";
import {
  buildDistributionCenterActionsColumn,
  indexDistributionCentersById,
  mapDistributionCentersToRows,
  type MasterDataDistributionCenterRow,
} from "./masterDataDistributionCenterTable.shared";

function buildColumns(
  onEdit: (entity: MasterDataDistributionCenterEntity) => void,
  centersById: Map<number, MasterDataDistributionCenterEntity>,
): TableColumnType<MasterDataDistributionCenterRow>[] {
  return [
    {
      title: "Purchaser Name",
      dataIndex: "purchaserName",
      key: "purchaserName",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 280,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "DC Name",
      dataIndex: "dcName",
      key: "dcName",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 280,
    },
    {
      title: "DC Code",
      dataIndex: "dcCode",
      key: "dcCode",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 180,
    },
    buildDistributionCenterActionsColumn(onEdit, centersById),
  ];
}

interface MasterDataDistributionCentersTableProps {
  distributionCenters: MasterDataDistributionCenterEntity[];
  search: string;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;
  onEdit: (entity: MasterDataDistributionCenterEntity) => void;
}

export const MasterDataDistributionCentersTable = ({
  distributionCenters,
  search,
  setSearch,
  setPage,
  onEdit,
}: MasterDataDistributionCentersTableProps) => {
  const centersById = useMemo(
    () => indexDistributionCentersById(distributionCenters),
    [distributionCenters],
  );

  const columns = useMemo(
    () => buildColumns(onEdit, centersById),
    [onEdit, centersById],
  );
  const dataSource = useMemo(
    () => mapDistributionCentersToRows(distributionCenters),
    [distributionCenters],
  );

  return (
    <MasterDataListTable
      columns={columns}
      dataSource={dataSource}
      search={search}
      entityLabel="distribution centers"
      ariaLabel="Purchaser Distribution Centers table"
      setSearch={setSearch}
      setPage={setPage}
    />
  );
};
